import React from "react";
import makeStyles from "@mui/styles/makeStyles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableFooter from "@mui/material/TableFooter";
import TablePagination from "@mui/material/TablePagination";
import Paper from "@mui/material/Paper";

import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import {
  ProgramTemplateDto,
  ProgramTemplateCategory,
  ProgramTemplateType,
  ProgramTemplateOrderingMode,
} from "@growth-machine-llc/stridist-api-client";
import ProgramTemplatesService from "../../services/ProgramTemplatesService";
import {
  AllOption,
  useProgramListFilterContext,
} from "../../contexts/ProgramLibraryFilterContext";
import { useDebounce } from "../../hooks/useDebounce";
import clsx from "clsx";
import { ProgramsTableRow } from "./ProgramsTableRow";
import { CategoryFilter } from "./filters/CategoryFilter";
import { ProgramsTableCardsView } from "./ProgramsTableCardsView";
import {
  ProgramLibraryCardSkeleton,
  ProgramLibraryTableSkeleton,
} from "../loading/ProgramLibrarySkeletons";
import { COACH_PROGRAM_LIBRARY_TEMPLATE_CURRICULUM_ROUTE } from "../../routes/routes";
import { useNavigate } from "react-router-dom";
import { JourneyAnchors } from "../../constants";
import { ProgramNameSortButton } from "../admin/ProgramNameSortButton";
import { ProgramTemplateSort } from "@growth-machine-llc/stridist-api-client";

export const getProgramTemplateLibraryWidths = () => {
  return {
    thumbnail: "6%",
    title: "48%",
    category: "18%",
    privacy: "18%",
    actions: "10%",
  };
};

const useStyles = makeStyles((theme) => ({
  root: {
    borderRadius: theme.spacing(1.5),
    padding: 1,
    boxShadow: theme.shadows[4],
    marginBottom: theme.spacing(3),
    [theme.breakpoints.down("md")]: {
      boxShadow: "none",
    },
  },

  container: {
    margin: 0,
    containerType: "inline-size",

    [theme.breakpoints.up("md")]: {
      margin: theme.spacing(1, 2),
    },
  },

  table: {
    "& th": {
      fontWeight: "bold",
      fontSize: 15,
    },

    "& td": {
      fontWeight: 500,
      fontSize: 14,
    },

    "& tfoot td": {
      borderBottom: "none",
    },
  },
}));

export interface ProgramsTableProps {
  status?: string;
}

const DEFAULT_ROWS_PER_PAGE = 10;
const DEFAULT_PAGE = 1;

export const COACH_PROGRAM_LIBRARY_ASSETS_QUERY_KEY = "program-library-assets";

export function ProgramsTable(props: ProgramsTableProps) {
  const s = useStyles();
  const columnWidths = getProgramTemplateLibraryWidths();

  const { breakpoints } = useTheme();
  const mdUp = useMediaQuery(breakpoints.up("md"));

  const [page, setPage] = React.useState(DEFAULT_PAGE);
  const [rowsPerPage, setRowsPerPage] = React.useState(DEFAULT_ROWS_PER_PAGE);

  const { query, category, templateType, handleCategoryChange } =
    useProgramListFilterContext();

  const navigate = useNavigate();

  const debounceQuery = useDebounce(query, 500);

  const [sort, setSort] = React.useState<ProgramTemplateSort | null>(null);

  const isAllCategoriesSelected = category === AllOption;

  const allCategories = React.useMemo(() => {
    return Object.values(ProgramTemplateCategory);
  }, []);

  const categories = React.useMemo(() => {
    if (isAllCategoriesSelected) {
      return allCategories;
    }
    if (Array.isArray(category)) {
      return category;
    }
    return category ? [category] : allCategories;
  }, [isAllCategoriesSelected, category, allCategories]);

  const templateTypeQueryParam =
    templateType === AllOption ? null : templateType;

  const categoryQueryParam =
    !isAllCategoriesSelected &&
    !Array.isArray(category) &&
    category !== AllOption
      ? (category as ProgramTemplateCategory)
      : null;

  const orderingMode = ProgramTemplateOrderingMode.Library;

  const { data: programTemplatesData, isFetching } = useQuery({
    queryKey: [
      COACH_PROGRAM_LIBRARY_ASSETS_QUERY_KEY,
      {
        page,
        rowsPerPage,
        query: debounceQuery,
        category: categoryQueryParam,
        templateType: templateTypeQueryParam,
        sort,
        orderingMode,
      },
    ],
    queryFn: () =>
      ProgramTemplatesService.getProgramTemplates(
        categoryQueryParam,
        templateTypeQueryParam,
        sort,
        orderingMode,
      ),
    placeholderData: keepPreviousData,
  });

  const allTemplates = programTemplatesData?.programTemplates || [];

  const filteredTemplates = React.useMemo(() => {
    let filtered = allTemplates;

    if (debounceQuery) {
      const lowerQuery = debounceQuery.toLowerCase();
      filtered = filtered.filter(
        (template) =>
          template.name?.toLowerCase().includes(lowerQuery) ||
          template.description?.toLowerCase().includes(lowerQuery),
      );
    }

    if (!isAllCategoriesSelected && categories.length > 0) {
      filtered = filtered.filter((template) =>
        categories.includes(template.category),
      );
    }

    return filtered;
  }, [allTemplates, debounceQuery, category, isAllCategoriesSelected]);

  const paginatedTemplates = React.useMemo(() => {
    const startIndex = (page - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return filteredTemplates.slice(startIndex, endIndex);
  }, [filteredTemplates, page, rowsPerPage]);

  const handleChangePage = (_, newPage) => {
    const newPageNumber = newPage + 1;
    setPage(newPageNumber);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(event.target.value);
    setPage(1);
  };

  const emptyRows = Array.from(new Array(rowsPerPage));

  const handleEditProgram = React.useCallback(
    (template: ProgramTemplateDto) => {
      if (template.slug && template.canEdit) {
        const link = COACH_PROGRAM_LIBRARY_TEMPLATE_CURRICULUM_ROUTE.replace(
          ":slug",
          template.slug,
        );
        navigate(link, { state: { title: template?.name } });
      }
    },
    [navigate],
  );

  return (
    <Paper className={s.root}>
      <Box className={s.container}>
        <Table className={s.table} aria-label="program table">
          {!mdUp ? (
            <Box
              display={"flex"}
              justifyContent={"space-between"}
              alignItems={"center"}
              width={"100%"}
              flexWrap={"wrap"}
            >
              <CategoryFilter
                onChange={handleCategoryChange}
                contextFilters={categories}
              />
            </Box>
          ) : (
            <TableHead className={JourneyAnchors.PROGRAM_LIBRARY_TABLE_HEADER}>
              <TableRow>
                <TableCell sx={{ width: columnWidths.thumbnail }}>
                  Thumbnail
                </TableCell>
                <TableCell sx={{ width: columnWidths.title }}>
                  <ProgramNameSortButton
                    sort={sort}
                    onChangeSort={(nextSort) => {
                      setSort(nextSort);
                      setPage(1);
                    }}
                  />
                </TableCell>
                <TableCell align="center" sx={{ width: columnWidths.category }}>
                  <CategoryFilter
                    onChange={handleCategoryChange}
                    contextFilters={categories}
                  />
                </TableCell>
                <TableCell align="center" sx={{ width: columnWidths.privacy }}>
                  Access
                </TableCell>
                <TableCell align="center" sx={{ width: columnWidths.actions }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
          )}
          {isFetching ? (
            emptyRows.map((_, index) => (
              <React.Fragment key={index}>
                {mdUp ? (
                  <ProgramLibraryTableSkeleton className={clsx(s.table)} />
                ) : (
                  <ProgramLibraryCardSkeleton />
                )}
              </React.Fragment>
            ))
          ) : (
            <>
              <TableBody>
                {paginatedTemplates.length > 0 ? (
                  paginatedTemplates.map((template, index) => (
                    <React.Fragment key={template.id}>
                      {mdUp ? (
                        <ProgramsTableRow
                          template={template}
                          onSelectedTemplate={handleEditProgram}
                          className={
                            index === 0
                              ? JourneyAnchors.PROGRAM_LIBRARY_FIRST_LIST_ITEM
                              : undefined
                          }
                          menuClassName={
                            index === 0
                              ? JourneyAnchors.PROGRAM_LIBRARY_FIRST_ITEM_MENU
                              : undefined
                          }
                        />
                      ) : (
                        <ProgramsTableCardsView
                          template={template}
                          onSelectedTemplate={handleEditProgram}
                          className={
                            index === 0
                              ? JourneyAnchors.PROGRAM_LIBRARY_FIRST_LIST_ITEM
                              : undefined
                          }
                        />
                      )}
                    </React.Fragment>
                  ))
                ) : (
                  <TableRow>
                    <TableCell align="center" colSpan={5}>
                      <Typography variant="body1" color="text.secondary">
                        No programs found for current filters.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </>
          )}
          <TableFooter>
            <TableRow>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                colSpan={5}
                count={filteredTemplates.length}
                rowsPerPage={rowsPerPage}
                page={Number(page - 1)}
                showFirstButton
                showLastButton
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </TableRow>
          </TableFooter>
        </Table>
      </Box>
    </Paper>
  );
}
